import React, { useEffect, useState } from 'react';
import { getLeaderboardAll } from '../../api';
import scorePicks from '../../utilties/scoring';

export default function Leaderboard() {
    const [data, setData] = useState(null);
    const [races, setRaces] = useState([]);

    useEffect(() => {
        getLeaderboardAll().then(setData);
    }, []);

    if (!data) {
        return (
            <div className="leaderboard-page">
                <h3>Leaderboard</h3>
                <div className="alert alert-secondary">No scores yet.</div>
            </div>
        );
    }

    const racesList = data.races || [];
    const users = data.users || [];

    // Determine which races have picks (at least one user with picks)
    const raceIdsWithPicks = new Set();
    users.forEach((user) => {
        if (user && user.racePicks) {
            Object.keys(user.racePicks).forEach((rid) => {
                raceIdsWithPicks.add(String(rid));
            });
        }
    });

    // Filter races to only those that have picks, then sort most recent -> oldest
    const racesWithPicks = (racesList || []).filter((r) => raceIdsWithPicks.has(String(r.id)));
    racesWithPicks.sort((a, b) => {
        const ak = Number(a.round || a.id || 0);
        const bk = Number(b.round || b.id || 0);
        return bk - ak; // most recent first
    });

    // Decide which races to display: if more than 5, show 4 most recent + 'Earlier' aggregate
    let displayRaces = racesWithPicks;
    let earlierRaces = [];
    if (racesWithPicks.length > 5) {
        displayRaces = racesWithPicks.slice(0, 4);
        earlierRaces = racesWithPicks.slice(4);
    }

    // build official results and dnfs arrays keyed by race id
    const officialByRace = {};
    const dnfsByRace = {};
    const allConsideredRaces = displayRaces.concat(earlierRaces);
    allConsideredRaces.forEach((race) => {
        const official = [];

        if (race && Array.isArray(race.results)) {
            race.results.forEach((rr) => {
                if (rr && typeof rr.position !== 'undefined') {
                    const posIndex = Number(rr.position) - 1;
                    official[posIndex] = rr.driver_id;
                }
            });
        }

        officialByRace[String(race.id)] = official;
        dnfsByRace[String(race.id)] = (race.dnfs || []).map((d) => d.driver_id);
    });

    // Build questions map by race id
    const questionsByRace = {};
    allConsideredRaces.forEach((race) => {
        questionsByRace[String(race.id)] = race.questions || [];
    });

    return (
        <div className="leaderboard-page">
            <h3>Leaderboard</h3>
            <table className="table table-striped">
                <thead>
                    <tr>
                        <th>Pos</th>
                        <th>User</th>
                        {displayRaces.map((r) => {
                            return <th key={r.id}>{(r.name || '').replace(/Grand Prix/gi, 'GP')}</th>;
                        })}
                        {earlierRaces && earlierRaces.length ? <th>Earlier</th> : null}
                        <th>Total</th>
                    </tr>
                </thead>
                <tbody>
                    {users.map((user) => {
                        let total = 0;

                        const cells = displayRaces.map((race) => {
                            const rp = user.racePicks && user.racePicks[String(race.id)] ? user.racePicks[String(race.id)] : null;
                            const picks = rp && Array.isArray(rp.picks) ? rp.picks : [];
                            const official = officialByRace[String(race.id)] || [];
                            const officialDnfs = dnfsByRace[String(race.id)] || [];
                            const userDnfPick = rp ? (rp.dnf !== undefined ? rp.dnf : undefined) : undefined;
                            const raceQuestions = questionsByRace[String(race.id)] || [];
                            const bonusAnswersStr = rp ? (rp.bonus_answers || null) : null;
                            const score = scorePicks(official, picks, officialDnfs, userDnfPick, raceQuestions, bonusAnswersStr);
                            total += score;
                            return (
                                <td key={race.id}>{score}</td>
                            );
                        });

                        let earlierCell = null;

                        if (earlierRaces && earlierRaces.length) {
                            let earlierSum = 0;

                            earlierRaces.forEach((race) => {
                                const rp = user.racePicks && user.racePicks[String(race.id)] ? user.racePicks[String(race.id)] : null;

                                const picks = rp && Array.isArray(rp.picks) ? rp.picks : [];

                                const official = officialByRace[String(race.id)] || [];
                                const officialDnfs = dnfsByRace[String(race.id)] || [];
                                const userDnfPick = rp ? (rp.dnf !== undefined ? rp.dnf : undefined) : undefined;
                                const raceQuestions = questionsByRace[String(race.id)] || [];
                                const bonusAnswersStr = rp ? (rp.bonus_answers || null) : null;

                                const scoreObj = scorePicks(official, picks, officialDnfs, userDnfPick, raceQuestions, bonusAnswersStr);

                                const score = typeof scoreObj === 'number' ? scoreObj : (scoreObj?.points ?? 0);

                                earlierSum += score;
                            });

                            total += earlierSum;

                            earlierCell = (
                                <td key="earlier"><strong>{earlierSum}</strong></td>
                            );
                        }

                        return { user, cells, earlierCell, total };
                    }).sort((a, b) => b.total - a.total).map((item, i) => (
                        <tr key={item.user.user_id}>
                            <td>{i + 1}</td>
                            <td><strong>{item.user.display_name || item.user.username}</strong></td>
                            {item.cells}
                            {item.earlierCell}
                            <td><strong>{item.total}</strong></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
